import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import Badge from "../../../../assets/hamaspik/Badge";
import { PifApprovalAuditEntry, PifApprovalChainEntry } from "../types";
import "./PifApprovalReview.scss";

interface PifApprovalChainRailProps {
  chain: PifApprovalChainEntry[];
  audit: PifApprovalAuditEntry[];
  // The "You" badge marks the logged-in viewer, not just whichever row is pending — a read-only
  // viewer (e.g. the submitter) must not see "You" next to the current approver's row (Bug 82446).
  viewerIsCurrentSigner: boolean;
}

const DOT_CLASS: Record<PifApprovalChainEntry["state"], string> = {
  done: "pif-approval-rail__dot--done",
  current: "pif-approval-rail__dot--current",
  waiting: "",
};

const PifApprovalChainRail: FC<PifApprovalChainRailProps> = ({ chain, audit, viewerIsCurrentSigner }) => {
  const showChain = chain.length > 0;
  const showAudit = audit.length > 0;
  if (!showChain && !showAudit) return null;

  return (
  <Box className="pif-approval-rail">
    {showChain && (
    <Box className="pif-approval-rail__card">
      <Typography className="pif-approval-rail__heading">SIGNATURE HISTORY</Typography>
      <Box className="pif-approval-rail__chain">
        {chain.map((c, i) => (
          // Keyed on the role, not the name: the name is the approver's and is blank on any row
          // the API hasn't resolved a signer for, which collided as a key (Bug 82350). The role is
          // unique per row — one signature each for the four signing roles, then the fifth
          // "Completed" stage (Epic 82838).
          <Box key={`${c.role}-${i}`} className="pif-approval-rail__chain-row">
            <Box className="pif-approval-rail__chain-track">
              <Box className={`pif-approval-rail__dot ${DOT_CLASS[c.state]}`}>{c.state === "done" ? "✓" : i + 1}</Box>
              {i < chain.length - 1 && (
                <Box className={`pif-approval-rail__line${c.state === "done" ? " pif-approval-rail__line--done" : ""}`} />
              )}
            </Box>
            <Box className={`pif-approval-rail__chain-body${c.state === "waiting" ? " pif-approval-rail__chain-body--waiting" : ""}`}>
              {/* Bug 82350 fixes this order for every workflow screen: role, then the approver's
                  first and last name, then the status. The name line is dropped rather than left
                  blank when the signature read gives no signer for the row. */}
              <Box className="pif-approval-rail__chain-role-row">
                <Typography className={`pif-approval-rail__chain-role${c.state === "current" ? " pif-approval-rail__chain-role--current" : ""}`}>
                  {c.role}
                </Typography>
                {c.state === "current" && viewerIsCurrentSigner && (
                  <Badge variant="primary" size="sm">
                    You
                  </Badge>
                )}
              </Box>
              {c.name && <Typography className="pif-approval-rail__chain-signer">{c.name}</Typography>}
              <Typography className="pif-approval-rail__chain-detail">{c.detail}</Typography>
              {c.stamp && <Typography className="pif-approval-rail__chain-stamp">{c.stamp}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
    )}

    {showAudit && (
    <Box className="pif-approval-rail__card">
      <Typography className="pif-approval-rail__heading">AUDIT TRAIL</Typography>
      <Box className="pif-approval-rail__audit">
        {audit.map((a, i) => (
          // Not keyed on `what` alone: with every review cycle kept (Bug 82910) the same action
          // repeats — "Department Director returned for changes" once per send-back.
          <Box key={`${a.what}-${i}`} className="pif-approval-rail__audit-row">
            <Typography className="pif-approval-rail__audit-what">{a.what}</Typography>
            <Typography className="pif-approval-rail__audit-when">{a.when}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
    )}
  </Box>
  );
};

export default PifApprovalChainRail;
